import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { useProject } from "../context/ProjectContext";
import { useAuth } from "../context/AuthContext";
import { ShieldCheck, Play, ArrowRight, FilePdf, FileXls, SlidersHorizontal, MagicWand } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { EquipmentSwitcher } from "../components/EquipmentSwitcher";
import DomainCard from "../components/validation/DomainCard";
import IssuesTable from "../components/validation/IssuesTable";
import { ClearBlockersDialog } from "../components/validation/ClearBlockersDialog";
import CustomRulesDialog from "../components/validation/CustomRulesDialog";
import FixGuideDialog from "../components/validation/FixGuideDialog";
import WhatIfBar from "../components/validation/WhatIfBar";

const BASE = process.env.REACT_APP_BACKEND_URL;

const SEV_ORDER = { critical: 0, major: 1, minor: 2, advisory: 3 };

function healthColor(s) {
  return s >= 90 ? "var(--c-green)" : s >= 70 ? "var(--c-accent)" : s >= 40 ? "var(--c-amber)" : "var(--c-red)";
}

export default function Validation({ embedded = false }) {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // a self-check finding links straight here: ?check=EM2 opens that issue with its impact
  const focusCheck = searchParams.get("check") || "";
  const { user } = useAuth();
  const { projects, pid, setPid } = useProject();
  const [packDomains, setPackDomains] = useState([]);
  const [results, setResults] = useState(null);
  const [valState, setValState] = useState("not_run");
  const [runningAll, setRunningAll] = useState(false);
  const [runningDomain, setRunningDomain] = useState(null);
  const [aiLoading, setAiLoading] = useState(null);
  const [acting, setActing] = useState(false);
  const [autoFixing, setAutoFixing] = useState(false);
  const [clearResult, setClearResult] = useState(null);
  const [guide, setGuide] = useState(null);         // { issue, guide } for the fix guide dialog

  const load = (id) => {
    if (!id) return;
    api.get(`/validation/${id}`).then(({ data }) => {
      setPackDomains(data.pack_domains);
      setResults(data.results);
      setValState(data.validation_state);
    }).catch((e) => toast.error(fmtErr(e)));
  };
  useEffect(() => { load(pid); }, [pid]);

  const runAll = async () => {
    setRunningAll(true);
    try {
      const doms = packDomains.filter((d) => d.registered);
      let last = null;
      let n = 0;
      for (const d of doms) {
        n += 1;
        setRunningDomain(d.id);
        toast.info(`Running validation ${n} of ${doms.length}: ${d.name}…`, { duration: 2000 });
        const { data } = await api.post(`/validation/${pid}/run/${d.id}`);
        last = data;
        setResults(data);  // cards update progressively as each domain completes
      }
      if (last) {
        setValState("run");
        toast.success(`Validation complete — health score ${last.health_score}`);
      }
    } catch (e) { toast.error(fmtErr(e), { duration: 7000 }); }
    finally { setRunningAll(false); setRunningDomain(null); }
  };

  const runDomain = async (domainId) => {
    setRunningDomain(domainId);
    try {
      const { data } = await api.post(`/validation/${pid}/run/${domainId}`);
      setResults(data);
      toast.success("Domain re-validated");
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setRunningDomain(null); }
  };

  const applyFix = async (issue, suggestion) => {
    setActing(true);
    try {
      const { data } = await api.post(`/validation/${pid}/issues/${encodeURIComponent(issue.id)}/apply-fix`,
        { inputs_patch: suggestion.inputs_patch || {}, specs_patch: suggestion.specs_patch || {} });
      setResults(data);
      const fr = data.fix_result;
      if (fr && !fr.resolved) {
        toast.warning(`${fr.check_id}: fix applied but the check still fails — opening the fix guide`, { duration: 7000 });
        setGuide({ issue, guide: null });
      } else if (fr) {
        const changes = Object.entries({ ...fr.applied_inputs, ...fr.applied_specs })
          .map(([k, v]) => `${k.replace(/_/g, " ")} → ${v}`).join(", ");
        toast.success(`${fr.check_id} resolved${changes ? ` — ${changes}` : ""}`, { duration: 7000 });
      } else {
        toast.success("Fix applied — recalculated and re-validated");
      }
    } catch (e) {
      const fg = e?.response?.data?.detail?.fix_guide;
      if (fg) {
        setGuide({ issue, guide: fg });
        toast.info(`${fg.check_id} needs a decision — here is what to change and where`, { duration: 6000 });
      } else {
        toast.error(fmtErr(e), { duration: 9000 });
      }
    }
    finally { setActing(false); }
  };

  const autoFix = async () => {
    setAutoFixing(true);
    try {
      const { data } = await api.post(`/validation/${pid}/recommendations/apply`);
      if (!data.ok) { toast.warning(data.reason || "Nothing to apply", { duration: 9000 }); return; }
      setResults(data);
      setClearResult(data);
      toast.success(data.message, { duration: 9000 });
    } catch (e) {
      const detail = e?.response?.data?.detail || {};
      const bl = detail.blockers || [];
      const extra = bl.length
        ? ` — ${bl.map((b) => `${b.check_id}: ${b.actual} vs ${b.required}`).join("; ")}`
        : "";
      toast.error(fmtErr(e) + extra, { duration: 12000 });
      if (detail.fix_guide) {
        const iss = allIssues.find((i) => i.check_id === detail.fix_guide.check_id);
        if (iss) setGuide({ issue: iss, guide: detail.fix_guide });
      }
    }
    finally { setAutoFixing(false); }
  };

  const waive = async (issue, justification) => {
    setActing(true);
    try {
      await api.post(`/validation/${pid}/issues/${encodeURIComponent(issue.id)}/waive`, { justification });
      toast.success("Issue waived");
      load(pid);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setActing(false); }
  };

  const askAi = async (issue) => {
    setAiLoading(issue.id);
    try {
      await api.post(`/validation/${pid}/issues/${encodeURIComponent(issue.id)}/ask-ai`);
      load(pid);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setAiLoading(null); }
  };

  const allIssues = useMemo(() => {
    if (!results) return [];
    return Object.values(results.domains || {})
      .flatMap((d) => d.issues || [])
      .sort((a, b) => (SEV_ORDER[a.severity] - SEV_ORDER[b.severity]) || a.id.localeCompare(b.id));
  }, [results]);

  const blockers = allIssues.filter((i) => i.status !== "waived" && (i.severity === "critical" || i.severity === "major"));
  const canWaive = user && ["lead", "project_manager", "admin"].includes(user.role);
  const project = projects.find((p) => p.id === pid);
  const [showRules, setShowRules] = useState(false);
  const [rules, setRules] = useState([]);

  const loadRules = () => {
    if (!project?.industry) return;
    api.get("/validation-rules", { params: { industry: project.industry } })
      .then(({ data }) => setRules(data.rules || []))
      .catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(loadRules, [project?.id]);

  const domainCards = rules.length
    ? [...packDomains, { id: "custom_rules", name: "Custom Rules (uploaded)", registered: true }]
    : packDomains;
  const noSuite = pid && packDomains.length > 0 && !packDomains.some((d) => d.registered);

  const proceed = async () => {
    try {
      await api.post(`/validation/${pid}/proceed`);
      toast.success("Validation approved — proceeding to Design Sheet");
      nav(`/projects/${pid}`);
    } catch (e) { toast.error(fmtErr(e), { duration: 8000 }); }
  };

  return (
    <div className={embedded ? "" : "p-6"} data-testid="validation-page">
      <div className="flex items-center gap-4 flex-wrap">
        {!embedded && (
          <>
            <div>
              <h2 className="text-base font-medium text-[var(--c-text)]">Validation &amp; Simulation</h2>
              <p className="text-xs text-[var(--c-muted)] mt-0.5">Domains load from the project's industry pack registry</p>
            </div>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="validation-project-select" className="w-96 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
                <SelectValue placeholder="Select project…" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">
                    <span className="font-mono text-[var(--c-muted)] mr-2">{p.project_code}</span>{p.product_summary}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <EquipmentSwitcher projectId={pid} onSwitched={() => load(pid)} />
          </>
        )}
        <div className="ml-auto flex gap-2">
          <Button data-testid="auto-fix-all-btn" onClick={autoFix}
            disabled={!results || autoFixing || blockers.length === 0}
            title={blockers.length ? `Apply every recommended change to clear ${blockers.length} blocking issue(s)` : "No blocking issues"}
            className="bg-[var(--c-surface)] border border-[var(--c-accent)]/50 text-[var(--c-accent)] hover:bg-[var(--c-accent)]/10 rounded-sm h-8 text-xs disabled:opacity-40">
            <MagicWand size={13} className="mr-1.5 text-[var(--c-ember)]" /> {autoFixing ? "Clearing…" : "Clear all blockers"}
          </Button>
          <Button data-testid="run-all-btn" onClick={runAll} disabled={runningAll || !pid || noSuite}
            className="bg-[var(--c-accent)] hover:bg-[var(--c-accent-hover)] text-white rounded-sm h-8 text-xs">
            <Play size={13} weight="bold" className="mr-1.5" /> {runningAll ? "Running…" : "Run All"}
          </Button>
          <Button data-testid="custom-rules-btn" variant="outline" onClick={() => setShowRules(true)} disabled={!pid}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
            <SlidersHorizontal size={13} className="mr-1.5" /> Custom Rules{rules.length ? ` (${rules.length})` : ""}
          </Button>
          <Button data-testid="export-pdf-btn" variant="outline" disabled={!results}
            onClick={() => window.open(`${BASE}/api/validation/${pid}/report?fmt=pdf`, "_blank")}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
            <FilePdf size={13} className="mr-1.5" /> Report PDF
          </Button>
          <Button data-testid="export-xlsx-btn" variant="outline" disabled={!results}
            onClick={() => window.open(`${BASE}/api/validation/${pid}/report?fmt=xlsx`, "_blank")}
            className="border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)] rounded-sm h-8 text-xs">
            <FileXls size={13} className="mr-1.5" /> Excel
          </Button>
          <Button data-testid="proceed-btn" onClick={proceed}
            disabled={!results || blockers.length > 0 || valState === "approved"}
            title={blockers.length ? `${blockers.length} blocking issue(s) must be fixed or waived` : "Proceed to Design Sheet"}
            className="bg-[var(--c-green)] hover:bg-[var(--c-green)]/85 text-white rounded-sm h-8 text-xs disabled:opacity-40">
            {valState === "approved" ? "Approved" : "Proceed to Design Sheet"} <ArrowRight size={13} className="ml-1.5" />
          </Button>
        </div>
      </div>

      {noSuite && (
        <div className="mt-6 bg-[var(--c-amber)]/10 border border-[var(--c-amber)]/40 rounded-sm p-5 text-sm text-[var(--c-text)]" data-testid="no-suite-note">
          No validation domains registered for <b>{project?.industry?.replace(/_/g, " ")}</b> yet. Power Transformers is the reference implementation.
        </div>
      )}

      {results && (
        <>
          <div className="mt-5 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-5" data-testid="health-card">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">Design health score</div>
                <div className="text-3xl font-mono font-medium mt-1" style={{ color: healthColor(results.health_score) }}
                  data-testid="health-score-value">{results.health_score}</div>
              </div>
              <div className="flex-1 min-w-48">
                <div className="h-2.5 bg-[var(--c-border)] rounded-sm overflow-hidden">
                  <div className="h-full rounded-sm transition-all duration-700" data-testid="health-score-bar"
                    style={{ width: `${results.health_score}%`, backgroundColor: healthColor(results.health_score) }} />
                </div>
                <div className="flex gap-4 mt-2 font-mono text-[10px]">
                  <span className="text-[var(--c-red)]" data-testid="count-critical">{results.counts.critical} critical</span>
                  <span className="text-[var(--c-amber)]" data-testid="count-major">{results.counts.major} major</span>
                  <span className="text-[var(--c-accent)]" data-testid="count-minor">{results.counts.minor} minor</span>
                  <span className="text-[var(--c-muted)]" data-testid="count-advisory">{results.counts.advisory} advisory</span>
                  <span className="text-[var(--c-muted)]" data-testid="count-waived">{results.counts.waived} waived</span>
                </div>
              </div>
              <div className="text-[10px] font-mono text-[var(--c-muted)]">ran {String(results.ran_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
          </div>

          <WhatIfBar projectId={pid} industry={project?.industry} health={results.health_score}
            onCommitted={(data) => setResults(data)} />

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4" data-testid="domain-grid">
            {domainCards.map((d) => (
              <DomainCard key={d.id} meta={d} result={results.domains?.[d.id] || null}
                running={runningDomain === d.id || runningAll} onRun={runDomain} />
            ))}
          </div>

          <div className="mt-5">
            <div className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)] mb-2">
              Issues · sorted by severity ({allIssues.length})
            </div>
            <IssuesTable issues={allIssues} canWaive={canWaive} onWaive={waive} onApplyFix={applyFix}
              onAskAi={askAi} aiLoading={aiLoading} acting={acting} projectId={pid}
              focusCheck={focusCheck} onGuide={(issue) => setGuide({ issue, guide: null })} />
          </div>
        </>
      )}

      {!results && !noSuite && pid && (
        <div className="mt-5 bg-[var(--c-surface)] border border-dashed border-[var(--c-border)] rounded-sm p-14 text-center" data-testid="validation-empty">
          <ShieldCheck size={28} className="mx-auto text-[var(--c-muted)]" />
          <div className="text-sm text-[var(--c-text)] mt-3">Not validated yet</div>
          <div className="text-xs text-[var(--c-muted)] mt-1">Press Run All to validate the current design across all domains.</div>
        </div>
      )}
      <ClearBlockersDialog open={!!clearResult} onOpenChange={(v) => !v && setClearResult(null)}
        result={clearResult} />
      <CustomRulesDialog open={showRules} onOpenChange={setShowRules} industry={project?.industry}
        onChanged={() => { loadRules(); toast.info("Custom rules updated — re-run validation to apply them"); }} />
      {guide && (
        <FixGuideDialog projectId={pid} issue={guide.issue} guide={guide.guide} open
          onOpenChange={(v) => { if (!v) setGuide(null); }}
          onApplied={(res) => (res ? setResults(res) : load(pid))} />
      )}
    </div>
  );
}
